import { useState } from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from "lucide-react";
import { BUSINESS, waLink } from "../lib/zoco";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `Hola ${BUSINESS.name}, soy ${form.name || "—"} (tel: ${
      form.phone || "—"
    }). ${form.message || "Me gustaría haceros una consulta."}`;
    window.open(waLink(text), "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-24 md:py-32 px-6 md:px-10 max-w-7xl mx-auto bg-[#F7F1E3]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Info */}
        <div className="lg:col-span-5">
          <p className="font-sans-z text-[10px] uppercase tracking-[0.32em] font-semibold text-[#034638] mb-5">
            Pasa a vernos
          </p>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight text-[#034638] leading-[1.0] mb-10">
            La Cala<br />
            <span className="italic">del Moral.</span>
          </h2>

          <ul className="space-y-7 mb-10">
            <li className="flex items-start gap-4">
              <MapPin size={22} strokeWidth={1.6} className="text-[#034638] mt-1 shrink-0" />
              <div>
                <p className="font-sans-z text-[10px] uppercase tracking-[0.28em] font-semibold text-[#5C6E6A] mb-1">
                  Dirección
                </p>
                <p className="font-display text-2xl tracking-tight text-[#1A2823]">
                  {BUSINESS.address}
                </p>
                <p className="font-sans-z text-base text-[#1A2823]/80">
                  {BUSINESS.city} · {BUSINESS.region}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Phone size={22} strokeWidth={1.6} className="text-[#034638] mt-1 shrink-0" />
              <div>
                <p className="font-sans-z text-[10px] uppercase tracking-[0.28em] font-semibold text-[#5C6E6A] mb-1">
                  Teléfono / WhatsApp
                </p>
                <a
                  href={`tel:+${BUSINESS.whatsappRaw}`}
                  data-testid="contact-phone-link"
                  className="font-display text-2xl tracking-tight text-[#1A2823] hover:text-[#034638] transition-colors"
                >
                  {BUSINESS.whatsappDisplay}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Mail size={22} strokeWidth={1.6} className="text-[#034638] mt-1 shrink-0" />
              <div>
                <p className="font-sans-z text-[10px] uppercase tracking-[0.28em] font-semibold text-[#5C6E6A] mb-1">
                  Email
                </p>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  data-testid="contact-email-link"
                  className="font-display text-2xl tracking-tight text-[#1A2823] hover:text-[#034638] transition-colors break-all"
                >
                  {BUSINESS.email}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Clock size={22} strokeWidth={1.6} className="text-[#034638] mt-1 shrink-0" />
              <div>
                <p className="font-sans-z text-[10px] uppercase tracking-[0.28em] font-semibold text-[#5C6E6A] mb-2">
                  Horario
                </p>
                <ul className="space-y-1">
                  {BUSINESS.hours.map((h) => (
                    <li key={h.days} className="font-sans-z text-sm text-[#1A2823]/85">
                      <span className="text-[#034638] font-semibold">{h.days}</span>
                      <span className="text-[#5C6E6A]"> · {h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          </ul>

          <div className="aspect-[16/10] w-full overflow-hidden border border-[#D9CFB6]">
            <iframe
              title="Ubicación Zoco"
              data-testid="contact-map"
              src={`https://www.google.com/maps?q=${BUSINESS.mapsQuery}&output=embed`}
              loading="lazy"
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
            />
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <div className="bg-[#EFE8D6] border border-[#D9CFB6] p-8 md:p-12">
            <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#034638] mb-3 leading-tight">
              Cuéntanos qué necesitas.
            </h3>
            <p className="font-sans-z text-base text-[#1A2823]/80 mb-8 leading-relaxed">
              Rellena el formulario y abriremos directamente WhatsApp con tu
              mensaje listo para enviar. Te responderemos en horario de tienda.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label
                  htmlFor="name"
                  className="block font-sans-z text-[10px] uppercase tracking-[0.28em] font-semibold text-[#5C6E6A] mb-2"
                >
                  Nombre
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  data-testid="contact-input-name"
                  className="w-full bg-transparent border-b border-[#D9CFB6] focus:border-[#034638] outline-none py-3 font-sans-z text-base text-[#1A2823] placeholder:text-[#5C6E6A]/60 transition-colors"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block font-sans-z text-[10px] uppercase tracking-[0.28em] font-semibold text-[#5C6E6A] mb-2"
                >
                  Teléfono
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="contact-input-phone"
                  className="w-full bg-transparent border-b border-[#D9CFB6] focus:border-[#034638] outline-none py-3 font-sans-z text-base text-[#1A2823] placeholder:text-[#5C6E6A]/60 transition-colors"
                  placeholder="+34 ..."
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block font-sans-z text-[10px] uppercase tracking-[0.28em] font-semibold text-[#5C6E6A] mb-2"
                >
                  Mensaje
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  value={form.message}
                  onChange={handleChange}
                  data-testid="contact-input-message"
                  className="w-full bg-transparent border-b border-[#D9CFB6] focus:border-[#034638] outline-none py-3 font-sans-z text-base text-[#1A2823] placeholder:text-[#5C6E6A]/60 transition-colors resize-none"
                  placeholder="Cuéntanos qué te gustaría pedir..."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#034638] text-white hover:bg-[#0B5A4A] transition-colors duration-300 font-sans-z text-[11px] tracking-[0.22em] uppercase font-semibold"
                >
                  <Send size={14} strokeWidth={2} />
                  Enviar por WhatsApp
                </button>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-direct-whatsapp"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#034638] text-[#034638] hover:bg-[#034638]/5 transition-colors duration-300 font-sans-z text-[11px] tracking-[0.22em] uppercase font-semibold"
                >
                  <MessageCircle size={14} strokeWidth={2} />
                  Abrir chat directo
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
